import { useEffect, useState } from "react";
import { theme, floatingSheet, btnPrimary, inputStyle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { FRAMEWORK_ORDER, FRAMEWORK_LABELS, FREE_REFERENCE_FRAMEWORKS, ESG_FRAMEWORK_IDS, hasPaidStandard, withAlternative, withoutOrphanedReferenceFrameworks, type FrameworkId } from "../context/FrameworksContext";
import { BUREAU_ADMIN_NOTE, submitErrorMessage } from "../pages/auditor/auditorShared";
import { PACKAGES, packageById, gbp, JERSEY_CEP_ANNUAL_PENCE, JERSEY_PACKAGE_ANNUAL_PENCE, JERSEY_PACKAGE_MONTHLY_PENCE } from "../lib/pricing";

// ── Shared modal shell ────────────────────────────────────────────────────

const overlay: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
};

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...floatingSheet, padding: 20, width: "100%", maxWidth: 480 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, paddingBottom: 12, borderBottom: "1px solid var(--c-line)" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SuccessBanner({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ background: theme.color.successBg, border: `1px solid ${theme.color.success}`, borderRadius: 3, padding: "12px 16px", fontSize: 13, color: theme.color.successText, lineHeight: 1.6 }}>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 600 }}>{label}</label>
      {children}
    </div>
  );
}

// ── Invite Client Wizard (name, email, size, frameworks, Jersey) ─────────────────────────────────────────


function StepIndicator({ step, total }: { step: number; total: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 20 }}>
      {Array.from({ length: total }, (_, i) => {
        const n = i + 1;
        const active = n === step;
        const done   = n < step;
        return (
          <div key={n} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{
              width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 700,
              background: done ? theme.color.successText : active ? theme.color.accent : theme.color.panel,
              color: done || active ? "#fff" : theme.color.faint,
              border: `1.5px solid ${done ? theme.color.successText : active ? theme.color.accent : theme.color.border}`,
            }}>{done ? "✓" : n}</div>
            {n < total && <div style={{ width: 20, height: 1, background: n < step ? theme.color.successText : theme.color.border }} />}
          </div>
        );
      })}
    </div>
  );
}

// Business size bands (mirror of backend/src/lib/businessSize.ts): UK definitions by headcount.
const BUSINESS_SIZES = [
  { id: "micro",      label: "Micro",      range: "1–9 people" },
  { id: "small",      label: "Small",      range: "10–49 people" },
  { id: "medium",     label: "Medium",     range: "50–249 people" },
  { id: "large",      label: "Large",      range: "250–999 people" },
  { id: "enterprise", label: "Enterprise", range: "1,000+ people" },
] as const;
type BusinessSizeId = (typeof BUSINESS_SIZES)[number]["id"];

// The Jersey edition (backend/src/lib/editions.ts) is jsy.abacassecurity.com: Cyber Essentials or
// CE+, the Cyber Security (Jersey) Law 2026, supplier risk and JCSC reporting, Jersey only. It is
// offered for a micro, small or medium business taking exactly one certificate (CE or CE+), by the
// platform owner or a consultancy holding the Jersey seat licence. The API enforces all of it.
const JERSEY_SIZES: ReadonlySet<BusinessSizeId> = new Set(["micro", "small", "medium"]);
const JERSEY_CERTIFICATES = new Set(["cyber_essentials", "cyber_essentials_plus"]);

function ChoiceButton({ selected, onClick, children, disabled = false, title }: {
  selected: boolean; onClick: () => void; children: React.ReactNode; disabled?: boolean; title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={selected}
      style={{
        fontSize: 11, padding: "4px 10px", borderRadius: 4, cursor: disabled ? "not-allowed" : "pointer",
        border: `1px solid ${selected ? theme.color.accent : theme.color.border}`,
        background: selected ? theme.color.accentBg : "transparent",
        color: selected ? theme.color.accentText : theme.color.muted,
        fontWeight: selected ? 600 : 400,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

const backButton: React.CSSProperties = { ...inputStyle, width: "auto", padding: "8px 16px", cursor: "pointer" };

export function InviteClientModal({ onClose, canInviteJersey: canInviteJerseyProp }: { onClose: () => void; canInviteJersey?: boolean }) {
  // Whether this consultancy may sell the Jersey edition (platform owner or seat licensee), from
  // GET /auth/me when the caller has not passed it. The API refuses a Jersey invite regardless.
  const [canInviteJerseyFetched, setCanInviteJerseyFetched] = useState(false);
  useEffect(() => {
    if (canInviteJerseyProp !== undefined) return;
    let cancelled = false;
    api.get<{ user: { canInviteJerseyEdition?: boolean } }>("/auth/me")
      .then((d) => { if (!cancelled) setCanInviteJerseyFetched(d.user.canInviteJerseyEdition ?? false); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canInviteJerseyProp]);
  const canInviteJersey = canInviteJerseyProp ?? canInviteJerseyFetched;
  const [step,         setStep]         = useState(1);
  const [orgName,      setOrgName]      = useState("");
  const [email,        setEmail]        = useState("");
  const [businessSize, setBusinessSize] = useState<BusinessSizeId | null>(null);
  // Sign-in policy (backend/src/lib/signInPolicy.ts): on unless the client has neither
  // Microsoft 365 nor Google Workspace. Once anyone joins, only the client changes it.
  const [ssoRequired,  setSsoRequired]  = useState(true);
  // The ESG standards are left out: the Environment & Sustainability Governance
  // add-on is what pays for and enrols them (backend lib/frameworks.ts).
  const frameworks = FRAMEWORK_ORDER.filter((id) => !ESG_FRAMEWORK_IDS.has(id)).map((id) => ({ id, name: FRAMEWORK_LABELS[id] }));
  const [selectedFws,  setSelectedFws]  = useState<string[]>([]);
  const [bundlePreset, setBundlePreset] = useState<string>("");
  const [isJersey,     setIsJersey]     = useState<boolean | null>(null);
  const [submitting,   setSubmitting]   = useState(false);
  const [done,         setDone]         = useState(false);
  const [error,        setError]        = useState("");

  function toggleFw(id: string) {
    setSelectedFws((prev) => {
      const removing = prev.includes(id);
      // Clear the micro CE option if CE is deselected
      if (removing && id === "cyber_essentials" && bundlePreset === "ce_annual") setBundlePreset("");
      // Deselecting the last paid standard takes the free reference frameworks (NIST CSF) with it.
      // Selecting CE Plus drops CE (and one IASME tier the others): one level of a scheme at a time.
      return removing ? withoutOrphanedReferenceFrameworks(prev.filter((x) => x !== id)) : withAlternative(prev, id);
    });
  }
  const paidSelected = hasPaidStandard(selectedFws);

  // Choosing a package pre-selects the standards its price covers (the
  // consultant can still untick one); the API puts the organisation on the
  // package's tier and add-ons itself.
  function choosePackage(id: string) {
    setBundlePreset(id);
    const pkg = packageById(id);
    if (!pkg) return;
    setSelectedFws((prev) => {
      const next = prev.filter((f) => !(f === "cyber_essentials_plus" && pkg.includedFrameworkIds.includes("cyber_essentials")));
      return [...next, ...pkg.includedFrameworkIds.filter((f) => !next.includes(f))];
    });
  }

  // The CE annual package is only valid when CE is the only paid standard selected
  // (regulatory overlays and the free reference frameworks may ride along — lib/bundles.ts).
  const showMicroCeOption = selectedFws.includes("cyber_essentials")
    && !selectedFws.includes("cyber_essentials_plus")
    && selectedFws.filter(id => !["cyber_essentials", "gdpr", "uk_gdpr", "jersey_dpjl"].includes(id) && !FREE_REFERENCE_FRAMEWORKS.has(id as FrameworkId)).length === 0;
  const chosenPackage = packageById(bundlePreset);

  // "Is this a Jersey business?" is asked only when the answer could be yes: a micro, small or
  // medium business enrolled in Cyber Essentials alone or Cyber Essentials Plus alone.
  const jerseyEligible = canInviteJersey
    && businessSize !== null && JERSEY_SIZES.has(businessSize)
    && selectedFws.length === 1 && JERSEY_CERTIFICATES.has(selectedFws[0]);
  const totalSteps = jerseyEligible ? 5 : 4;
  const jersey = jerseyEligible && isJersey === true;

  async function handleSubmit() {
    setError(""); setSubmitting(true);
    try {
      if (jersey) {
        // The edition carries its own package; the API sets the tier, TPRM, Jersey and the term.
        await api.post("/invites/client", { orgName, contactEmail: email, businessSize, edition: "jersey", frameworkIds: selectedFws, ssoRequired });
      } else {
        const sbPackage = bundlePreset === "ce_annual";
        const bundleTier = ["small", "medium", "large", "enterprise"].includes(bundlePreset) ? bundlePreset : undefined;
        await api.post("/invites/client", {
          orgName,
          contactEmail: email,
          businessSize,
          frameworkIds: selectedFws,
          ssoRequired,
          ...(sbPackage  ? { smallBusinessPackage: true } : {}),
          ...(bundleTier ? { bundleTier } : {}),
        });
      }
      setDone(true);
    } catch (err: unknown) {
      const e = err as { payload?: { error?: string }; message?: string };
      setError(apiErrorMessage(e, e?.message || "Failed to send invite"));
    } finally {
      setSubmitting(false);
    }
  }

  const stepTitles = ["Organisation", "Contact", "Business size", "Frameworks", "Jersey"];
  const sendButton = (
    <button
      disabled={submitting || (step === 5 && isJersey === null)}
      onClick={handleSubmit}
      style={{ ...btnPrimary, opacity: submitting || (step === 5 && isJersey === null) ? 0.5 : 1 }}
    >
      {submitting ? "Sending…" : "Send invite"}
    </button>
  );

  return (
    <ModalShell title={done ? "Invite Sent" : `Invite New Client — ${stepTitles[step - 1]}`} onClose={onClose}>
      {done ? (
        <SuccessBanner>
          Invite sent to <strong>{email}</strong>. They'll receive a link to set up <strong>{orgName}</strong>'s account
          {jersey ? <> on <strong>jsy.abacassecurity.com</strong></> : null}.
        </SuccessBanner>
      ) : (
        <>
          <StepIndicator step={step} total={totalSteps} />

          {step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Field label="Name of business">
                <input
                  style={inputStyle} value={orgName} autoFocus required
                  onChange={(e) => setOrgName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && orgName.trim() && setStep(2)}
                  placeholder="Acme Ltd"
                />
              </Field>
              <div style={{ fontSize: 12, color: theme.color.subtle }}>
                This is the name that will appear throughout the platform for this client.
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button disabled={!orgName.trim()} onClick={() => setStep(2)} style={{ ...btnPrimary, opacity: orgName.trim() ? 1 : 0.4 }}>
                  Next →
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ fontSize: 13, color: theme.color.subtle }}>
                Setting up <strong style={{ color: theme.color.text }}>{orgName}</strong>
              </div>
              <Field label="Invite email">
                <input
                  style={inputStyle} type="email" value={email} autoFocus required
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && email.trim() && setStep(3)}
                  placeholder="jane@acme.com"
                />
              </Field>
              <div style={{ fontSize: 12, color: theme.color.subtle }}>
                An invite link will be sent to this address so they can set up their account.
              </div>
              <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: theme.color.text, cursor: "pointer" }}>
                <input type="checkbox" checked={ssoRequired} onChange={(e) => setSsoRequired(e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  <strong>Require single sign-on</strong> (recommended). Everyone signs in with their Microsoft 365 or Google
                  Workspace work account. Untick only if the client has neither; they then use passwords with an authenticator app.
                  The client's own administrators can change this later.
                </span>
              </label>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setStep(1)} style={backButton}>← Back</button>
                <button disabled={!email.trim()} onClick={() => setStep(3)} style={{ ...btnPrimary, opacity: email.trim() ? 1 : 0.4 }}>
                  Next →
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Field label="Size of business">
                <div style={{ display: "grid", gap: 6 }}>
                  {BUSINESS_SIZES.map((s) => {
                    const sel = businessSize === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setBusinessSize(s.id)}
                        aria-pressed={sel}
                        style={{
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          padding: "9px 12px", borderRadius: 3, cursor: "pointer", fontSize: 13, fontFamily: "inherit",
                          border: `1px solid ${sel ? theme.color.accent : theme.color.border}`,
                          background: sel ? theme.color.accentBg : "transparent",
                          color: sel ? theme.color.accentText : theme.color.text,
                          fontWeight: sel ? 600 : 400,
                        }}
                      >
                        <span>{s.label}</span>
                        <span style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 400 }}>{s.range}</span>
                      </button>
                    );
                  })}
                </div>
              </Field>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setStep(2)} style={backButton}>← Back</button>
                <button disabled={!businessSize} onClick={() => setStep(4)} style={{ ...btnPrimary, opacity: businessSize ? 1 : 0.4 }}>
                  Next →
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ fontSize: 13, color: theme.color.subtle }}>
                Enrol <strong style={{ color: theme.color.text }}>{orgName}</strong> in compliance frameworks. You can add more later.
              </div>
              {frameworks.length > 0 && (
                <Field label="Frameworks">
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {frameworks.map((fw) => {
                      // A free reference framework (NIST CSF) rides along with a paid standard.
                      const needsStandard = FREE_REFERENCE_FRAMEWORKS.has(fw.id) && !paidSelected;
                      return (
                        <ChoiceButton
                          key={fw.id}
                          selected={selectedFws.includes(fw.id)}
                          onClick={() => !needsStandard && toggleFw(fw.id)}
                          disabled={needsStandard}
                          title={needsStandard ? "Free alongside any standard — select a standard first" : undefined}
                        >
                          {fw.name}{FREE_REFERENCE_FRAMEWORKS.has(fw.id) ? " · free" : ""}
                        </ChoiceButton>
                      );
                    })}
                  </div>
                </Field>
              )}
              <div style={{ borderTop: `1px solid ${theme.color.border}`, paddingTop: 12, marginTop: 4 }}>
                <label style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 600, display: "block", marginBottom: 6 }}>
                  Pricing Bundle
                </label>
                <select value={bundlePreset} onChange={(e) => choosePackage(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                  <option value="">No bundle — à la carte (standard rates, Basic Self-Service to start)</option>
                  {PACKAGES.filter((p) => p.id !== "ce_annual" || showMicroCeOption).map((p) => (
                    <option key={p.id} value={p.id}>{p.option}</option>
                  ))}
                </select>
                {chosenPackage && (
                  <p style={{ fontSize: 11, color: theme.color.subtle, marginTop: 6, lineHeight: 1.5 }}>{chosenPackage.summary}</p>
                )}
                {jerseyEligible && (
                  <p style={{ fontSize: 11, color: theme.color.subtle, marginTop: 6, lineHeight: 1.5 }}>
                    Next you'll be asked whether this is a Jersey business — a Jersey business carries its own package instead of a bundle.
                  </p>
                )}
              </div>
              {error && !jerseyEligible && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setStep(3)} style={backButton}>← Back</button>
                {jerseyEligible
                  ? <button onClick={() => setStep(5)} style={btnPrimary}>Next →</button>
                  : sendButton}
              </div>
            </div>
          )}

          {step === 5 && jerseyEligible && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading }}>Is this a Jersey business?</div>
              <div style={{ display: "flex", gap: 8 }}>
                <ChoiceButton selected={isJersey === true} onClick={() => setIsJersey(true)}>Yes</ChoiceButton>
                <ChoiceButton selected={isJersey === false} onClick={() => setIsJersey(false)}>No</ChoiceButton>
              </div>
              {isJersey === true && (
                <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6, padding: "10px 12px", background: theme.color.accentBg, borderRadius: 3 }}>
                  <strong style={{ color: theme.color.text }}>{orgName}</strong> will be set up on <strong style={{ color: theme.color.text }}>jsy.abacassecurity.com</strong> and
                  sign in there: {selectedFws[0] === "cyber_essentials_plus" ? "Cyber Essentials Plus" : "Cyber Essentials"}, the Cyber Security (Jersey) Law 2026
                  and OES, supplier risk and JCSC incident reporting — Jersey only, no data protection module. It is on the Jersey package
                  ({gbp(JERSEY_PACKAGE_ANNUAL_PENCE)} a year, or {gbp(JERSEY_PACKAGE_MONTHLY_PENCE)} a month — the client chooses
                  {selectedFws[0] === "cyber_essentials_plus" ? `, plus ${gbp(JERSEY_CEP_ANNUAL_PENCE)} a year for Cyber Essentials Plus` : ""}) rather than
                  {bundlePreset ? " the bundle you chose" : " a bundle"} — or, for a licensed consultancy, covered by your seat licence.
                </div>
              )}
              {isJersey === false && (
                <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 }}>
                  {orgName} will be set up on the main platform (app.abacassecurity.com) with the frameworks and bundle you chose.
                </div>
              )}
              {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setStep(4)} style={backButton}>← Back</button>
                {sendButton}
              </div>
            </div>
          )}
        </>
      )}
    </ModalShell>
  );
}

// ── Invite Auditor Modal ──────────────────────────────────────────────────
// A platform-owner action (POST /invites/auditor is requirePlatformOwner):
// provisions a person into an existing certification bureau. Opened from the
// Platform Admin invites tab; bureaux come from the admin list the page
// already uses (GET /admin/auditors), never from the client-scoped
// /organizations/bureaus marketplace.

interface Bureau {
  id: string;
  name: string;
  accreditation?: string | null;
  accreditationBody?: string | null;
  /** Accredited framework ids (BureauFramework); the server enforces the accreditation regardless. */
  frameworks?: string[];
}

const AUDITOR_ROLE_OPTIONS = [
  { value: "lead_auditor",         label: "Lead Auditor" },
  { value: "auditor",              label: "Auditor" },
  { value: "technical_auditor",    label: "Technical Auditor" },
  { value: "internal_auditor",     label: "Internal Auditor" },
  { value: "observer",             label: "Observer" },
  { value: "framework_specialist", label: "Framework Specialist" },
];

export function InviteAuditorModal({ onClose }: { onClose: () => void }) {
  const [email,       setEmail]       = useState("");
  const [name,        setName]        = useState("");
  const [bureauId,    setBureauId]    = useState("");
  const [auditorRole, setAuditorRole] = useState("auditor");
  // Framework specialists only: limited to the chosen bureau's accreditation.
  const [frameworkIds, setFrameworkIds] = useState<string[]>([]);
  const [bureaus,     setBureaus]     = useState<Bureau[]>([]);
  const [submitting,  setSubmitting]  = useState(false);
  const [done,        setDone]        = useState(false);
  const [error,       setError]       = useState("");

  useEffect(() => {
    api.get<{ bureaux: Bureau[] }>("/admin/auditors")
      .then((d) => { setBureaus(d.bureaux); if (d.bureaux.length) setBureauId(d.bureaux[0].id); })
      .catch(() => {});
  }, []);

  const isSpecialist = auditorRole === "framework_specialist";
  const selectedBureau = bureaus.find((b) => b.id === bureauId);
  // GET /admin/auditors carries each bureau's accreditation; should it be
  // missing, the server still rejects any framework outside it, so offer the
  // full list in that case.
  const accreditationKnown = Array.isArray(selectedBureau?.frameworks);
  const accredited: string[] = accreditationKnown ? (selectedBureau?.frameworks ?? []) : FRAMEWORK_ORDER;
  const toggleFw = (id: string) =>
    setFrameworkIds((prev) => prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!bureauId) return;
    setError(""); setSubmitting(true);
    try {
      await api.post("/invites/auditor", {
        email, name, bureauId, auditorRole,
        frameworkIds: isSpecialist ? frameworkIds.filter((f) => accredited.includes(f)) : [],
      });
      setDone(true);
    } catch (err: unknown) {
      // The bureau's own invite page answers the same codes with the same sentences.
      setError(submitErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title="Invite Auditor" onClose={onClose}>
      {done ? (
        <SuccessBanner>
          Invite sent to <strong>{email}</strong>. They'll receive a link to create their auditor account.
        </SuccessBanner>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field label="Full name">
            <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} autoFocus required placeholder="Jane Smith" />
          </Field>
          <Field label="Email">
            <input style={inputStyle} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="jane@bsi.co.uk" />
          </Field>
          <Field label="Bureau">
            {bureaus.length === 0 ? (
              <div style={{ fontSize: 12, color: theme.color.subtle }}>No bureaux found. Add a certification bureau on the Auditors tab first.</div>
            ) : (
              <select style={inputStyle} value={bureauId} onChange={(e) => { setBureauId(e.target.value); setFrameworkIds([]); }}>
                {bureaus.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}{(b.accreditationBody ?? b.accreditation) ? ` (${b.accreditationBody ?? b.accreditation})` : ""}</option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Role">
            <select style={inputStyle} value={auditorRole} onChange={(e) => setAuditorRole(e.target.value)}>
              {AUDITOR_ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
          {isSpecialist && (
            <Field label="Framework access">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {accredited.map((id) => {
                  const sel = frameworkIds.includes(id);
                  return (
                    <button
                      key={id} type="button" onClick={() => toggleFw(id)}
                      style={{
                        fontSize: 11, padding: "4px 10px", borderRadius: 4, cursor: "pointer",
                        border: `1px solid ${sel ? theme.color.accent : theme.color.border}`,
                        background: sel ? theme.color.accentBg : "transparent",
                        color: sel ? theme.color.accentText : theme.color.muted,
                        fontWeight: sel ? 600 : 400,
                      }}
                    >
                      {FRAMEWORK_LABELS[id as FrameworkId] ?? id}
                    </button>
                  );
                })}
              </div>
              {accredited.length === 0 ? (
                <div style={{ fontSize: 11, color: theme.color.dangerText, marginTop: 2 }}>This bureau has no accredited frameworks yet.</div>
              ) : frameworkIds.length === 0 ? (
                <div style={{ fontSize: 11, color: theme.color.dangerText, marginTop: 2 }}>Select at least one framework.</div>
              ) : !accreditationKnown && (
                <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>Only standards within the bureau's accreditation are accepted.</div>
              )}
            </Field>
          )}
          <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>{BUREAU_ADMIN_NOTE}</div>
          {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{ ...inputStyle, width: "auto", padding: "8px 16px", cursor: "pointer" }}>Cancel</button>
            <button
              type="submit"
              disabled={submitting || !name.trim() || !email.trim() || !bureauId || (isSpecialist && frameworkIds.length === 0)}
              style={{ ...btnPrimary, opacity: submitting ? 0.7 : 1 }}
            >
              {submitting ? "Sending…" : "Send invite"}
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

// ── Invite Staff Modal ────────────────────────────────────────────────────

const CONSULTANT_ROLE_OPTIONS = [
  { value: "lead_consultant",   label: "Lead Consultant" },
  { value: "senior_consultant", label: "Senior Consultant" },
  { value: "junior_consultant", label: "Junior Consultant" },
  { value: "framework_specialist", label: "Framework Specialist" },
  { value: "report_writer",     label: "Report Writer" },
];

export function InviteStaffModal({ onClose }: { onClose: () => void }) {
  const [email,          setEmail]          = useState("");
  const [displayName,    setDisplayName]    = useState("");
  const [consultantRole, setConsultantRole] = useState("senior_consultant");
  const [frameworkIds,   setFrameworkIds]   = useState<string[]>([]);
  const [submitting,     setSubmitting]     = useState(false);
  const [done,           setDone]           = useState(false);
  const [error,          setError]          = useState("");

  function toggleFw(id: string) {
    setFrameworkIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      await api.post("/invites/staff", {
        email,
        displayName,
        consultantRole,
        frameworkIds: consultantRole === "framework_specialist" ? frameworkIds : [],
      });
      setDone(true);
    } catch (err: unknown) {
      const e = err as { payload?: { error?: string }; message?: string };
      setError(apiErrorMessage(e, e?.message || "Failed to send invite"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title="Invite Staff Member" onClose={onClose}>
      {done ? (
        <SuccessBanner>
          Invite sent to <strong>{email}</strong>. They'll receive a link to set up their account and access your company's compliance portal.
        </SuccessBanner>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontSize: 13, color: theme.color.muted, lineHeight: 1.6 }}>
            Invite a colleague to join your consultancy as a consultant on the Abacas Security Compliance Platform.
          </div>
          <Field label="Full name">
            <input style={inputStyle} value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoFocus required placeholder="Jane Smith" />
          </Field>
          <Field label="Email">
            <input style={inputStyle} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="jane@yourcompany.com" />
          </Field>
          <Field label="Role">
            <select style={inputStyle} value={consultantRole} onChange={(e) => { setConsultantRole(e.target.value); setFrameworkIds([]); }}>
              {CONSULTANT_ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
          {consultantRole === "framework_specialist" && (
            <Field label="Framework access">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {FRAMEWORK_ORDER.map((id) => {
                  const sel = frameworkIds.includes(id);
                  return (
                    <button
                      key={id} type="button" onClick={() => toggleFw(id)}
                      style={{
                        fontSize: 11, padding: "4px 10px", borderRadius: 4, cursor: "pointer",
                        border: `1px solid ${sel ? theme.color.accent : theme.color.border}`,
                        background: sel ? theme.color.accentBg : "transparent",
                        color: sel ? theme.color.accentText : theme.color.muted,
                        fontWeight: sel ? 600 : 400,
                      }}
                    >
                      {FRAMEWORK_LABELS[id]}
                    </button>
                  );
                })}
              </div>
              {frameworkIds.length === 0 && (
                <div style={{ fontSize: 11, color: theme.color.dangerText, marginTop: 2 }}>Select at least one framework.</div>
              )}
            </Field>
          )}
          {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{ ...inputStyle, width: "auto", padding: "8px 16px", cursor: "pointer" }}>Cancel</button>
            <button
              type="submit"
              disabled={submitting || !displayName.trim() || !email.trim() || (consultantRole === "framework_specialist" && frameworkIds.length === 0)}
              style={{ ...btnPrimary, opacity: submitting ? 0.7 : 1 }}
            >
              {submitting ? "Sending…" : "Send invite"}
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}
